import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";

import { SectionHeading } from "@/components/Layout/Parts";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import Text from "@/components/Text";
import { SortState, TestingCompetitionBonusResource } from "@/types/resources";
import { api } from "@/utils/api";
import { SortDirection } from "@/utils/enums";
import ApprovedBonusesTableRow from "./ApprovedBonusesTableRow";
import GamificationDateFilter from "./GamificationDateFilter";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "approved_at",
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    font-weight: 700;
    &:nth-child(1) {
      width: 20%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 16%;
    }
    &:nth-child(3) {
      width: 12%;
    }
    &:nth-child(4) {
      width: 11%;
    }
    &:nth-child(5) {
      width: 11%;
    }
    &:nth-child(6) {
      width: 14%;
    }
    &:nth-child(7) {
      width: 8%;
    }
    &:nth-child(8) {
      width: 8%;
      padding-right: 24px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
`;

export const months = (() => {
  const months = [];

  let currentDate = DateTime.now();

  while (currentDate.diffNow("months").months > -12) {
    months.push(currentDate);
    currentDate = currentDate.minus({ months: 1 });
  }

  return months;
})();

interface ActivePeriod {
  type: "month" | "period";
  period: string;
}

export const getDateFiltersQueryParams = (activePeriod: ActivePeriod) => {
  if (activePeriod.type === "month") {
    return {
      dateFrom: activePeriod.period,
      dateTo: DateTime.fromFormat(activePeriod.period, "yyyy-LL-dd")
        .endOf("month")
        .toFormat("yyyy-LL-dd"),
    };
  }

  if (activePeriod.period === "-90 days") {
    const now = DateTime.now();

    return {
      dateFrom: now.minus({ days: 89 }).toFormat("yyyy-LL-dd"),
      dateTo: now.toFormat("yyyy-LL-dd"),
    };
  }

  return null;
};

const ApprovedBonusesTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [activePeriod, setActivePeriod] = useState<ActivePeriod>({
    type: "month",
    period: months[0].startOf("month").toFormat("yyyy-LL-dd"),
  });

  const dateParams = getDateFiltersQueryParams(activePeriod);

  const {
    data: bonuses,
    isLoading,
    isError,
  } = useQuery<TestingCompetitionBonusResource[]>(
    [
      "bonuses",
      {
        type: "thumbnails",
        status: "approved",
        sort,
        period: activePeriod.period,
      },
    ],
    () =>
      api.get<never, TestingCompetitionBonusResource[]>(
        "/v1/gamification/thumbnails/bonuses",
        {
          params: {
            "filter[approved]": 1,
            ...(sort.direction !== "DEFAULT" && {
              sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
            }),
            ...(!!dateParams && {
              "filter[date_from]": dateParams.dateFrom,
              "filter[date_to]": dateParams.dateTo,
            }),
          },
        }
      ),
    {
      staleTime: 1000 * 60,
    }
  );

  return (
    <>
      <Header>
        <StyledSectionHeading>Approved bonuses</StyledSectionHeading>
        <GamificationDateFilter
          activePeriod={activePeriod}
          setActivePeriod={setActivePeriod}
        />
      </Header>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Project Name
                <br />
                <Text as="span" $color="gray" $type="extraSmall">
                  Project ID
                </Text>
              </Th>
              <SortableTh
                property="team_name"
                title="Team"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="c_batch"
                title="Test Sequence"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="ended_at"
                title="Date Closed"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="approved_at"
                title="Date Approved"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="user_name"
                title="Participant Name"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="ctr"
                title="CTR"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="reward"
                title="Bonus"
                setSort={setSort}
                sort={sort}
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={8}
              isError={isError}
              hasData={!!bonuses?.length}
            >
              {bonuses?.map((bonus) => (
                <ApprovedBonusesTableRow key={bonus.id} bonus={bonus} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
    </>
  );
};

export default ApprovedBonusesTable;
